// #SPLIT_REVENUE_V1 — shared helpers, moved out of App.js unchanged so it and RevenueModule.js both use them.
import { slimFor as _slimFor, REVENUE_RANK_KEY, slimRevenueRank } from "./fieldSlim";
import { STAFF_NAMES_KEY, slimStaffNames } from "./staffName";
import { HOTEL_FIELD_KEY, slimHotelField } from "./perDiem";
import { HR_WAGES_KEY, mergePay, splitPay } from "./hrWages";
import { useLiveKeys as _useLiveKeysShared } from "./liveKeys";
import { isoWeek } from "./weekOf";
import { useState, useEffect } from "react";

// #PM_CANON_V1 — collapse free-text PM values to a constant canonical name. Non-PMs (Titan, Other, Ronald, blank) → null = excluded from PM totals.
export const PM_CANON_RULES = [
  [/PERRY|\bPG\b/, 'Perry Graff'],
  [/CORY|TATE|\bCT\b/, 'Cory Tate'],
  [/CHRIS|JURCIK|\bCJ\b/, 'Chris Jurcik'],
  [/RUBEN|MENDEZ|\bRM\b/, 'Ruben Mendez'],
];

export function pmCanon(raw){ const s=String(raw||'').toUpperCase(); for(const [re,name] of PM_CANON_RULES){ if(re.test(s)) return name; } return null; }

export const SVC = {E:'Electric',W:'Water',G:'Gas',F:'Fiber',S:'Sewer',P:'Pipeline'};

export const PMS = ['AU','CT','CJ','RM','PG'];

export const PM_NAMES = {CT:'Cory Tate', CJ:'Chris Jurcik', PG:'Perry Graff', RM:'Ruben Mendez', AU:'Auger'};

export const STATUS_CLR = {
  'Approved':   ['#BFDBFE','#1E40AF'],
  'Called In':  ['#1E3A8A','#FFFFFF'],
  'In Progress':['#C2410C','#FFFFFF'],
  'On Hold':    ['#CA8A04','#FFFFFF'],
  'Complete':   ['#166534','#FFFFFF'],
  'Cancelled':  ['#991B1B','#FFFFFF']
};

export const f$ = n => n ? `$${Math.round(n).toLocaleString()}` : '—';

export const fAbbr = n => !n ? '—' : n >= 1e6 ? `$${(n/1e6).toFixed(1)}M` : n >= 1e3 ? `$${Math.round(n/1e3)}K` : `$${Math.round(n)}`;

export const fmtD = d => { if (!d) return '—'; try { const dt = new Date(d+'T12:00:00'); return `${String(dt.getMonth()+1).padStart(2,'0')}/${String(dt.getDate()).padStart(2,'0')}/${String(dt.getFullYear()).slice(2)}`; } catch { return d; } };

export const todayStr = () => (()=>{const d=new Date();return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;})();

// ══════════════════════════════════════════════════════════
//  SUPABASE STORAGE
// ══════════════════════════════════════════════════════════
export const SUPA_URL = 'https://aufabqhyubdimqpxknsy.supabase.co';

export const SUPA_KEY = 'sb_publishable_UD5un6D8nrCLg-ohjZ-olw_s2wY5R07';

// #AUTH_JWT_V1 — Present the signed-in user's Supabase JWT so RLS sees 'authenticated' instead of
// 'anon'. Entra SSO already mints a real Supabase session (dope_sso); we simply never used it for
// database calls. Falls back to the publishable key while the legacy anon policy is still in place,
// so this deploy is safe to ship BEFORE the anon policy is dropped.
export let _refreshing = null;

export let _lastRefreshSoft = false;

// #AUTH_NET_BLIP_V1 — last renew failed for network/server reasons, not a rejected sign-in
// #AUTH_EXPIRED_V1 (2026-10-08) — Jeffrey Kuhn landed on the red "couldn't confirm your role" banner with
// half the dashboard blank. Supabase logs: every read from his browser came back 401 and no refresh was
// ever attempted. The old check trusted only our locally-computed s.exp; if that said "still good" (or the
// stored session had no refresh token) we kept sending a dead token, every sg() quietly returned null,
// role-map looked empty and he fell to SUP. Now: (a) the token's OWN exp claim must also be in the future,
// (b) a 401 forces one refresh + retry, (c) if the session can't be revived we say "sign-in expired" with a
// one-click Sign in again — instead of pretending it's a role problem.
export const _jwtExpMs = (jwt) => {
  try { const p = JSON.parse(atob(String(jwt).split('.')[1].replace(/-/g,'+').replace(/_/g,'/'))); return p && p.exp ? p.exp * 1000 : 0; }
  catch { return 0; }
};

export let _authDead = false;

export const _flagAuthDead = () => {
  // #AUTH_FALSE_ALARM_V1 (2026-10-08) — no stored sign-in at all = signed out / on the login screen (e.g. the
  // weekly sign-in just cleared it). A leftover background request 401ing then is expected, not a problem.
  try { const _s = JSON.parse(localStorage.getItem('dope_sso') || 'null'); if (!_s || !_s.access_token) return; } catch { return; }
  if (_authDead) return; _authDead = true;
  _authEvt('expired');   // #AUTH_HEALTH_LOG_V1
  try { window.dispatchEvent(new Event('dope-auth-expired')); } catch {}
};

// #AUTH_HEALTH_LOG_V1 — a dead session can't write to Supabase, so problems queue on the device and post to
// the shared 'auth-health' key the next time that person is signed in. Owner sees them on the Dash.
export const _authEvt = (kind, extra) => {
  try {
    const q = JSON.parse(localStorage.getItem('dope_auth_events') || '[]');
    const last = q[q.length - 1];
    if (last && last.kind === kind && Date.now() - Date.parse(last.at) < 3600000) return;   // one per hour per kind
    let _lu = ''; try { _lu = localStorage.getItem('dope_last_upn') || ''; } catch {}
    q.push({ at: new Date().toISOString(), upn: _currentUpn() || _lu || '(unknown)', kind,   // #AUTH_FALSE_ALARM_V1
      ua: String(navigator.userAgent || '').slice(0, 120), ...(extra || {}) });
    localStorage.setItem('dope_auth_events', JSON.stringify(q.slice(-50)));
    // #AUTH_ALERT_V1 (2026-10-08) — Bill: "would be great to know if someone is having issues so i can get a
    // hold of them and help." Real problems go straight to /api/auth-alert, which logs them server-side and
    // texts + emails Bill right away (works even though this person's own sign-in is dead). If that post
    // fails, the event stays queued and _flushAuthEvts posts it once they're signed in again.
    const ev = q[q.length - 1];
    if (ev && ev.kind !== 'weekly-signin' && /@dakotautility\.com$/i.test(ev.upn || '')) {
      fetch('/api/auth-alert', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(ev) })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d && d.logged) { try {
          const q2 = JSON.parse(localStorage.getItem('dope_auth_events') || '[]').filter(x => !(x.at === ev.at && x.kind === ev.kind));
          localStorage.setItem('dope_auth_events', JSON.stringify(q2)); } catch {} } })
        .catch(() => {});
    }
  } catch {}
};

export const _accessToken = async (force, failedTok) => {
  try {
    const raw = localStorage.getItem('dope_sso'); if (!raw) return null;
    const s = JSON.parse(raw); if (!s || !s.access_token) return null;
    const jwtExp = _jwtExpMs(s.access_token);
    // #AUTH_RETRY_ALL_V1 — another request already refreshed since this one was sent: use that token.
    if (force && failedTok && s.access_token !== failedTok && (!jwtExp || jwtExp > Date.now() + 30000)) return s.access_token;
    if (!force && s.exp && s.exp > Date.now() + 30000 && (!jwtExp || jwtExp > Date.now() + 30000)) return s.access_token;   // still valid (30s skew) #AUTH_EXPIRED_V1
    if (!s.refresh_token) { _flagAuthDead(); return null; }   // #AUTH_EXPIRED_V1
    if (!_refreshing) {
      _refreshing = (async () => {
        try {
          const r = await fetch(`${SUPA_URL}/auth/v1/token?grant_type=refresh_token`, {
            method: 'POST',
            headers: { 'apikey': SUPA_KEY, 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: s.refresh_token }),
          });
          if (r.status >= 500 || r.status === 429) { _lastRefreshSoft = true; return null; }   // #AUTH_NET_BLIP_V1
          const d = await r.json();
          if (d && d.access_token) {
            _lastRefreshSoft = false;
            const sess = { access_token: d.access_token, refresh_token: d.refresh_token || s.refresh_token,
              exp: Date.now() + ((d.expires_in ? +d.expires_in : 3600) * 1000), email: s.email, signedInAt: s.signedInAt };
            try { localStorage.setItem('dope_sso', JSON.stringify(sess)); } catch {}
            _authDead = false;
            return sess.access_token;
          }
        } catch { _lastRefreshSoft = true; return null; }   // #AUTH_NET_BLIP_V1 — no network: not dead, try on the next call
        _lastRefreshSoft = false;
        _flagAuthDead();   // refresh token rejected/missing — session can't be revived #AUTH_EXPIRED_V1
        return null;
      })();
      _refreshing.finally(() => { _refreshing = null; });
    }
    const p = _refreshing;
    return await p;
  } catch { return null; }
};

export const _supaHdr = async () => {
  const tok = await _accessToken();
  return { 'apikey': SUPA_KEY, 'Authorization': `Bearer ${tok || SUPA_KEY}`, 'Content-Type': 'application/json' };
};

export const _sgRaw = async k => {
  try {
    const url = `${SUPA_URL}/rest/v1/app_storage?key=eq.${encodeURIComponent(k)}&select=value`;
    // #AUTHED_RELOAD_V1 (2026-10-09) — no sign-in on this device = don't ask. An unsigned read is not refused
    // by Supabase, it comes back EMPTY, which looked exactly like "no data saved" (Perry / Chris J 10/9).
    const _tok0 = await _accessToken();
    if (!_tok0) return null;
    let r = await fetch(url, { headers: { 'apikey': SUPA_KEY, 'Authorization': `Bearer ${_tok0}`, 'Content-Type': 'application/json' } });
    if (r.status === 401) {   // #AUTH_EXPIRED_V1 — dead token: force one refresh, retry once
      const t = await _accessToken(true);
      if (t) r = await fetch(url, { headers: { 'apikey': SUPA_KEY, 'Authorization': `Bearer ${t}`, 'Content-Type': 'application/json' } });
      if (r.status === 401) { _flagAuthDead(); return null; }
    }
    const d = await r.json();
    if (d && d.length > 0) return JSON.parse(d[0].value);
    return null;
  } catch { return null; }
};

export const _ssRaw = async (k, v) => {
  try {
    const body = JSON.stringify({ key: k, value: JSON.stringify(v), updated_at: new Date().toISOString() });
    let r = await fetch(`${SUPA_URL}/rest/v1/app_storage`, {
      method: 'POST',
      headers: { ...(await _supaHdr()), 'Prefer': 'resolution=merge-duplicates' },
      body
    });
    if (r.status === 401) {   // #AUTH_EXPIRED_V1 — a save must never silently vanish on a dead token
      const t = await _accessToken(true);
      if (t) r = await fetch(`${SUPA_URL}/rest/v1/app_storage`, { method: 'POST',
        headers: { 'apikey': SUPA_KEY, 'Authorization': `Bearer ${t}`, 'Content-Type': 'application/json', 'Prefer': 'resolution=merge-duplicates' }, body });
      if (r.status === 401) { _flagAuthDead(); return false; }
    }
    // #FIELD_SLIM_LISTS_V1 — saving HR or the fleet master also refreshes the slim copy field screens read (no wages, no costs).
    if (r.ok) { try { const _sl = _slimFor(k, v); if (_sl && _sl[1]) ss(_sl[0], _sl[1]); } catch {} }
    if (r.ok && k === 'revenue-input') { try { const _rr = slimRevenueRank(v); if (_rr) _ssRaw(REVENUE_RANK_KEY, _rr); } catch {} }   // #SUP_STANDING_V1 — standing index, no dollars
    if (r.ok && k === 'hr-employees') { try { _ssRaw(STAFF_NAMES_KEY, slimStaffNames(v)); } catch {} }
    if (r.ok && k === 'hotel-requests') { try { _ssRaw(HOTEL_FIELD_KEY, slimHotelField(v)); } catch {} }   // #FIELD_HOTEL_PERDIEM_V1 — booked stays, no costs, for the supervisor dash   // #STAFF_CALNAME_V1 — names-only copy for the calendars
    return r.ok ? true : 'denied';   // #HR_WAGE_SPLIT_V1 — callers only test truthy; the wage split needs to know a refused save
  } catch { return false; }
};

// #HR_WAGE_SPLIT_V1 (2026-10-20) — pay lives in 'hr-wages' (Owner / HR / Payroll only). Every other key passes straight through.
export const sg = async (k) => {
  const v = await _sgRaw(k);
  if (k === 'hr-employees' && Array.isArray(v)) {
    const w = await _sgRaw(HR_WAGES_KEY);
    if (w && typeof w === 'object' && !Array.isArray(w)) return mergePay(v, w);
  }
  return v;
};

export const ss = async (k, v) => {
  if (k === 'hr-employees' && Array.isArray(v)) {
    const { stripped, pay } = splitPay(v);
    if (Object.keys(pay).length) {
      let ok = false;
      try {
        const cur = await _sgRaw(HR_WAGES_KEY);
        const base = (cur && typeof cur === 'object' && !Array.isArray(cur)) ? cur : {};
        ok = (await _ssRaw(HR_WAGES_KEY, { ...base, ...pay })) === true;
      } catch { ok = false; }
      return _ssRaw(k, ok ? stripped : v);   // pay couldn't be filed in hr-wages → keep the record whole; nothing is lost
    }
  }
  return _ssRaw(k, v);
};

// #LIVE_KEYS_V1 — efficient near-live refresh. Instead of re-downloading big blobs on a timer, poll
// ONLY key+updated_at (a few bytes/key) for the keys a screen cares about; when a key's timestamp
// advances (someone else saved), fire onChange(changedKeys) so that screen re-pulls just that data.
// Change-gated + focus/visibility catch-up. Never fires on first sight (mount already loaded).
// #LIVE_KEYS_SHARED_V1 (2026-09-24) — every call site below is unchanged; they now all feed ONE shared
// 12s poller in ./liveKeys (one request per tick for the union of watched keys, instead of one per call).
export function useLiveKeys(keys, onChange) {
  _useLiveKeysShared(keys, onChange, { supaUrl: SUPA_URL, supaKey: SUPA_KEY, getToken: _accessToken });
}

// ══════════════════════════════════════════════════════════
//  COLORS & DESIGN
// ══════════════════════════════════════════════════════════
export const C = {
  bg:'#F0F2F5', surface:'#FFFFFF', card:'#FFFFFF',
  border:'#E2E8F0', accent:'#E85D24', blue:'#1D4ED8',
  green:'#15803D', yellow:'#B45309', red:'#DC2626',
  text:'#111827', muted:'#6B7280', white:'#111827'
};

export function StatusBadge({ status }) {
  const [bg, clr] = STATUS_CLR[status] || ['#1F2937','#9CA3AF'];
  return (
    <span style={{ background:bg, color:clr, padding:'2px 7px', borderRadius:4,
      fontSize:10, fontWeight:700, letterSpacing:0.5, textTransform:'uppercase',
      whiteSpace:'nowrap', fontFamily:"Calibri, Arial, sans-serif" }}>
      {status}
    </span>
  );
}

export function Btn({ onClick, children, variant='primary', small, style: extStyle }) {
  const base = {
    cursor:'pointer', border:'none', borderRadius:6, fontWeight:600,
    fontFamily:"Calibri, Arial, sans-serif", display:'inline-flex', alignItems:'center',
    gap:5, whiteSpace:'nowrap', transition:'opacity .15s',
    padding: small ? '5px 10px' : '8px 14px',
    fontSize: small ? 12 : 13,
  };
  const variants = {
    primary:{ background:C.accent, color:'#fff' },
    secondary:{ background:C.card, color:C.text, border:`1px solid ${C.border}` },
    ghost:{ background:'transparent', color:C.muted },
    danger:{ background:'#4c1d24', color:C.red },
    success:{ background:'#14532d', color:C.green },
  };
  return <button style={{ ...base, ...variants[variant], ...extStyle }} onClick={onClick}>{children}</button>;
}

export function ProgressBar({ value, max, color = C.accent }) {
  const pct = max ? Math.min(100, Math.round(value/max*100)) : 0;
  return (
    <div style={{ height:6, background:C.border, borderRadius:3, overflow:'hidden' }}>
      <div style={{ height:'100%', width:`${pct}%`, background:color, borderRadius:3,
        transition:'width .3s' }} />
    </div>
  );
}

// Type & Month auto-fill — hidden on entry form, kept for tables
export const _revNum = (v) => { const n = parseFloat(String(v==null?'':v).replace(/[^0-9.\-]/g,'')); return isNaN(n)?0:n; };

// #AR_MOBILE_V1 (2026-10-16) — phone = under 900px, same break as the rest of DOPE
export const _isPhoneNow = () => typeof window !== 'undefined' && window.innerWidth < 900;

export const PENDING_REV = { view: null };

// ── Week helpers (Mon-Sun, ISO style) ────────────────────
// #WEEKNUM_V1 — single definition now lives in ./weekOf. This wrapper keeps the existing
// string-in call sites working (daily log, exports, revenue grid) without touching them.
export function isoWeekNum(dateStr) {
  if (!dateStr) return 0;
  return isoWeek(new Date(dateStr + 'T12:00:00'));
}

export function _currentUpn() {
  // Resolve the signed-in user's email. Prefer the stored field; if it's missing
  // (stale session saved before the email field existed), decode it from the SSO
  // token so the user still resolves and isn't wrongly dropped to Foreman. #UPN_FIX_V1
  const _fromJwt = (jwt) => {
    try {
      const part = jwt.split('.')[1].replace(/-/g,'+').replace(/_/g,'/');
      const p = JSON.parse(atob(part));
      const md = p.user_metadata || {};
      const cc = md.custom_claims || {};
      return (p.email || md.email || md.preferred_username || md.upn
        || cc.upn || cc.preferred_username || cc.email || cc.unique_name
        || p.preferred_username || p.upn || '').toLowerCase();
    } catch { return ''; }
  };
  try {
    const s = JSON.parse(localStorage.getItem('dope_sso')||'null');
    if (s) {
      if (s.email) return String(s.email).toLowerCase();
      if (s.access_token) { const e = _fromJwt(s.access_token); if (e) return e; }
    }
  } catch {}
  try { const p = JSON.parse(localStorage.getItem('dope_auth')||'null'); if (p && p.email) return String(p.email).toLowerCase(); } catch {}
  return '';
}

// #MOBILE_ROTATE_V1 (2026-10-08) — a phone in landscape (touch screen under 500px tall) is NOT a desktop, even though
// it's wider than 768px. Re-checks on orientationchange too (iOS reports the old size for a beat after rotating).
export const _isDeskNow = () => {
  const w = window.innerWidth, h = window.innerHeight;
  const coarse = !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
  return w >= 768 && !(coarse && h < 500);
};

export function useIsDesktop() {
  const [desk, setDesk] = useState(_isDeskNow);
  useEffect(() => {
    const h = () => setDesk(_isDeskNow());
    const ho = () => { h(); setTimeout(h, 300); };
    window.addEventListener('resize', h);
    window.addEventListener('orientationchange', ho);
    return () => { window.removeEventListener('resize', h); window.removeEventListener('orientationchange', ho); };
  }, []);
  return desk;
}
